import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStore } from "app/actions";
import { RootState } from "app/store";
import { addDevice, fetchDevices, removeDevice } from "app/thunks/devicesAPI";
import { Headset } from "interfaces/Device";
import { handleTableDataSorting, SortProps } from "utils/table/table";

export interface State {
  list: any[];
  headsets: Headset[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isFetching: boolean;
  error: any;
}

export enum DEVICES_TYPES {
  HEADSET = "HEADSET",
  TABLET = "TABLET",
}

const initialState: State = {
  list: [],
  headsets: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  isFetching: false,
  error: undefined,
};

export const devicesSlice = createSlice({
  name: "devices",
  initialState,
  reducers: {
    sortDevices: (state, action: PayloadAction<SortProps>) => {
      const { sortBy, sortOrder } = action.payload;
      if (state.list.length > 0 && sortBy && sortOrder) {
        const sorted = [...state.list];
        sorted.sort(
          handleTableDataSorting<any>({
            sortBy,
            sortOrder,
            dateProperties: ["createdAt"],
          }),
        );
        state.list = sorted;
      }
    },
    resetFlags: (state) => {
      state.isSuccess = false;
      state.isLoading = false;
      state.isError = false;
      state.isFetching = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetStore, () => initialState)
      .addCase(fetchDevices.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(
        fetchDevices.fulfilled,
        (state, action: PayloadAction<Headset[]>) => {
          state.isSuccess = true;
          state.isFetching = false;
          //!To.DO ADD PAYLOAD DATA FORMATTING FOR DEVICES
          state.list = action.payload;
          state.headsets = action.payload;
        },
      )
      .addCase(addDevice.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addDevice.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(addDevice.fulfilled, (state, action: PayloadAction<any>) => {
        state.isSuccess = true;
        state.isLoading = false;
        state.list.push(action.payload);
      })
      .addCase(removeDevice.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeDevice.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(removeDevice.fulfilled, (state, action) => {
        state.isSuccess = true;
        state.isLoading = false;

        state.list = state.list.filter(
          (device) => device._id !== action.payload._id,
        );
      });
  },
});

export const { sortDevices, resetFlags } = devicesSlice.actions;

// ------ Selectors ------
export const getDeviceActionState = (state: RootState) => ({
  devices: state.devices.list,
  headsets: state.devices.headsets,
  isFetching: state.devices.isFetching,
  isLoading: state.devices.isLoading,
  isSuccess: state.devices.isSuccess,
  isError: state.devices.isError,
  error: state.devices.error,
});

export default devicesSlice.reducer;
